import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select, Textarea } from "@/components/base/FormControls";
import { useAdministrators } from "@/store/administrators/AdministratorsProvider";
import {
  ADMINISTRATEUR_ROLE_OPTIONS,
  ADMINISTRATEUR_STATUT_OPTIONS,
  type AdministrateurStatut,
  type PlatformAdministrateur,
  type PlatformAdministrateurInput,
} from "@/store/administrators/types";

interface AdministrateurFormProps {
  open: boolean;
  onClose: () => void;
  administrateur: PlatformAdministrateur | null;
}

interface FormState {
  prenom: string;
  nom: string;
  email: string;
  telephone: string;
  role: string;
  statut: AdministrateurStatut;
  notes: string;
}

const EMPTY_STATE: FormState = {
  prenom: "",
  nom: "",
  email: "",
  telephone: "",
  role: "super_admin",
  statut: "actif",
  notes: "",
};

export default function AdministrateurForm({ open, onClose, administrateur }: AdministrateurFormProps) {
  const { updateAdministrateur } = useAdministrators();
  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    if (administrateur) {
      setForm({
        prenom: administrateur.prenom,
        nom: administrateur.nom,
        email: administrateur.email,
        telephone: administrateur.telephone,
        role: administrateur.role,
        statut: administrateur.statut,
        notes: administrateur.notes,
      });
    } else {
      setForm(EMPTY_STATE);
    }
    setErrors({});
  }, [open, administrateur]);

  const update = (patch: Partial<FormState>) => setForm((prev) => ({ ...prev, ...patch }));

  const handleSubmit = () => {
    const nextErrors: Record<string, string> = {};
    if (!form.prenom.trim()) nextErrors.prenom = "Le prénom est obligatoire.";
    if (!form.nom.trim()) nextErrors.nom = "Le nom est obligatoire.";
    if (!form.email.trim()) nextErrors.email = "L'email est obligatoire.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) nextErrors.email = "Email invalide.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const input: PlatformAdministrateurInput = {
      prenom: form.prenom.trim(),
      nom: form.nom.trim(),
      email: form.email.trim(),
      telephone: form.telephone.trim(),
      role: form.role,
      statut: form.statut,
      notes: form.notes.trim(),
    };

    if (administrateur) updateAdministrateur(administrateur.id, input);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Modifier l'administrateur"
      description="Gestion des comptes administratifs internes de la plateforme. La création de super administrateurs reste gérée hors interface."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-save-line text-base" aria-hidden="true" />
            Enregistrer
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
        <Field label="Prénom" htmlFor="adm-prenom" required error={errors.prenom}>
          <Input
            id="adm-prenom"
            value={form.prenom}
            onChange={(event) => update({ prenom: event.target.value })}
            invalid={Boolean(errors.prenom)}
            maxLength={60}
          />
        </Field>
        <Field label="Nom" htmlFor="adm-nom" required error={errors.nom}>
          <Input
            id="adm-nom"
            value={form.nom}
            onChange={(event) => update({ nom: event.target.value })}
            invalid={Boolean(errors.nom)}
            maxLength={60}
          />
        </Field>
        <Field label="Email" htmlFor="adm-email" required error={errors.email}>
          <Input
            id="adm-email"
            type="email"
            value={form.email}
            onChange={(event) => update({ email: event.target.value })}
            invalid={Boolean(errors.email)}
          />
        </Field>
        <Field label="Téléphone" htmlFor="adm-telephone">
          <Input
            id="adm-telephone"
            value={form.telephone}
            onChange={(event) => update({ telephone: event.target.value })}
            maxLength={30}
          />
        </Field>
        <Field label="Rôle" htmlFor="adm-role">
          <Select
            id="adm-role"
            value={form.role}
            onChange={(event) => update({ role: event.target.value })}
            options={ADMINISTRATEUR_ROLE_OPTIONS}
          />
        </Field>
        <Field label="Statut" htmlFor="adm-statut">
          <Select
            id="adm-statut"
            value={form.statut}
            onChange={(event) => update({ statut: event.target.value as AdministrateurStatut })}
            options={ADMINISTRATEUR_STATUT_OPTIONS}
          />
        </Field>
        <Field label="Notes" htmlFor="adm-notes" className="sm:col-span-2">
          <Textarea
            id="adm-notes"
            value={form.notes}
            onChange={(event) => update({ notes: event.target.value })}
            className="min-h-[80px]"
            maxLength={400}
          />
        </Field>
      </div>
    </Modal>
  );
}